"use client";

import NavBar from "@/components/nav";
import { Card, CardBody, CardHeader, CircularProgress, Input } from "@heroui/react";
import { useSession } from "next-auth/react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Suspense, useEffect, useState } from "react";
import { apiFetch } from "@/lib/api";
import { IconBriefcase2, IconCarambola } from "@tabler/icons-react";
import DashboardTabs from "./dashboardTabs";
import ViewProjectModal from "./ViewProjectModal";

// Componentes de render
import ProfileCard from "./renders/ProfileCard";
import ProjetosList from "./renders/ProjectsList";
import BadgesAndSigns from "./renders/BadgesAndSigns";
import ActiveProjectsList from "./renders/ActiveProjectsList";

interface Curso {
  id_curso: string;
  nome: string;
  descricao: string;
}

interface Projetos {
  descricao: string;
  habilitado: boolean;
  id_projeto: string;
  id_usuario: string;
  imagem_atividade: string;
  tags: string[];
  texto: string;
  titulo: string;
  participando: boolean;
  percentual_conclusao: number;
  data_inicio: string;
  data_fim: string;
  meu_status: string;
  status: string;
}

type TagLevel = "iniciante" | "intermediario" | "maximo" | "ametista";

interface Levels {
  descricao: string;
  id_level: string;
  nivel: number;
  tag: TagLevel;
  titulo: string;
  xp_max: number;
  xp_min: number;
}

export default function Dashboard() {
  const [isLogoutLoading, setIsLogoutLoading] = useState(false);
  const { data: session, status } = useSession();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [curso, setCurso] = useState<Curso>({
    id_curso: session?.user.curso,
    nome: "",
    descricao: "",
  });

  const [projetos, setProjetos] = useState<Projetos[]>([]);
  const [levels, setLevels] = useState<Levels[]>([]);
  const [activeTab, setActiveTab] = useState<"home" | "habilidades" | "projetos" | "perfil">("home");
  const [searchTerm, setSearchTerm] = useState("");

  const openViewProjectModal = (id_projeto: string) => {
    const params = new URLSearchParams(searchParams.toString());

    params.set("modal", "viewProject");
    params.set("projectId", id_projeto);

    router.push(`${pathname}?${params.toString()}`, { scroll: false });
  };

  // carregar modal de registro completo se new === true
  useEffect(() => {
    if (status !== "authenticated") return;
    if (!session?.user?.new) return;

    router.push(`${pathname}?modal=completeRegistration`);
  }, [status, session, router, pathname]);

  // GET em cursos
  const getCurso = async () => {
    const res = await apiFetch(`/cursos/${curso.id_curso}`, {
      method: "GET",
      headers: { Authorization: `Bearer ${session?.accessToken}` },
    });
    const data = await res.json();
    setCurso((prev) => ({
      ...prev,
      nome: data.nome,
      descricao: data.descricao,
    }));
  };

  useEffect(() => {
    if (!session) return;

    getCurso();
  }, [session, curso.id_curso]);

  // trazer levels localmente
  useEffect(() => {
    if (session?.user?.level) {
      setLevels(session.user.level as Levels[]);
    }
  }, [session, router]);

  const levelAtual = levels.find((lvl) => lvl.id_level === session?.user?.id_level_atual);

  // GET de projetos
  const loadProjetos = async () => {
    const res = await apiFetch("/projetos", {
      method: "GET",
      headers: {
        Authorization: `Bearer ${session?.accessToken}`,
        "Content-Type": "application/json",
      },
    });

    if (res.ok) {
      const data = await res.json();
      setProjetos(data);
    }
  };

  useEffect(() => {
    if (!session) return;

    loadProjetos();
  }, [session]);

  const filteredProjetos = projetos.filter(
    (projeto) =>
      projeto.titulo.toLowerCase().includes(searchTerm.toLowerCase()) || projeto.descricao.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div className="flex min-h-screen w-screen flex-col">
      <Suspense fallback={null}>
        <NavBar hasLogoutLoading={false} setIsLogoutLoading={setIsLogoutLoading} />
        <ViewProjectModal />
      </Suspense>

      <div className="px-3 container mx-auto flex-1 flex flex-col gap-6 py-8 overflow-x-hidden">
        {!isLogoutLoading ? (
          <DashboardTabs
            value={activeTab}
            onChange={setActiveTab}
            children={{
              home: (
                <div className="flex flex-col gap-6">
                  <ProfileCard
                    userImage={session?.user.image}
                    userNome={session?.user.nome}
                    cursoNome={curso.nome}
                    poloNome="Polo São Mateus"
                    levelAtual={levelAtual}
                    xpTotal={session?.user.xp_total ?? 0}
                  />
                  <ActiveProjectsList />
                </div>
              ),
              habilidades: (
                <div className="flex flex-col gap-6">
                  <ProfileCard
                    userImage={session?.user.image}
                    userNome={session?.user.nome}
                    cursoNome={curso.nome}
                    poloNome="Polo São Mateus"
                    levelAtual={levelAtual}
                    xpTotal={session?.user.xp_total ?? 0}
                  />

                  <Card className="w-full p-4 md:p-6">
                    <CardHeader className="flex flex-row gap-2">
                      <div className="rounded-full bg-emerald-500/10 p-2 flex items-center justify-center">
                        <IconCarambola size={22} className="text-emerald-500" />
                      </div>
                      <h1 className="text-2xl md:text-3xl font-bold">Habilidades</h1>
                    </CardHeader>
                    <CardBody>EM CONSTRUÇÃO</CardBody>
                  </Card>
                </div>
              ),
              projetos: (
                <div className="flex flex-col gap-6">
                  <Card className="p-2 md:p-5">
                    <CardHeader className="flex flex-col md:flex-row md:justify-between gap-4 md:gap-0">
                      <div className="flex flex-row gap-2 items-center w-full md:w-auto">
                        <div className="rounded-full bg-emerald-500/10 p-2 flex items-center justify-center">
                          <IconBriefcase2 size={22} className="text-emerald-500" />
                        </div>
                        <h1 className="font-bold text-2xl">Projetos</h1>
                      </div>

                      <div className="w-full md:w-1/3">
                        <Input
                          type="text"
                          size="sm"
                          placeholder="Buscar por título ou descrição..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                    </CardHeader>

                    <CardBody className="flex flex-col gap-4 md:grid md:grid-cols-2">
                      {filteredProjetos.length > 0 ? (
                        <ProjetosList lista={filteredProjetos} onOpenViewProject={openViewProjectModal} />
                      ) : (
                        <p className="text-center text-gray-500 text-sm">Nenhum projeto encontrado com esse termo.</p>
                      )}
                    </CardBody>
                  </Card>
                </div>
              ),
              perfil: (
                <div className="flex flex-col gap-6">
                  <ProfileCard
                    userImage={session?.user.image}
                    userNome={session?.user.nome}
                    cursoNome={curso.nome}
                    poloNome="Polo São Mateus"
                    levelAtual={levelAtual}
                    xpTotal={session?.user.xp_total ?? 0}
                  />

                  <BadgesAndSigns />
                </div>
              ),
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <CircularProgress size="lg" />
          </div>
        )}
      </div>
    </div>
  );
}
